import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, message,
  Card, Tag, Space, Popconfirm, Progress, Row, Col, Statistic
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, BarChartOutlined, SyncOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import FbUsageCell from '../components/FbUsageCell';

const { Option } = Select;

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [stats, setStats] = useState([]);
  const [editingApp, setEditingApp] = useState(null);
  const [syncingAppId, setSyncingAppId] = useState(null);
  const [form] = Form.useForm();

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/applications');
      setApplications(response.data || []);
    } catch (error) {
      message.error('获取应用列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleSubmit = async (values) => {
    try {
      if (editingApp) {
        await api.put(`/applications/${editingApp.id}`, values);
        message.success('更新成功');
      } else {
        await api.post('/applications', values);
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingApp(null);
      fetchApplications();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
    }
  };

  const handleEdit = async (record) => {
    try {
      setLoading(true);
      const response = await api.get(`/applications/${record.id}`);
      setEditingApp(record);
      form.setFieldsValue({
        name: response.data.name,
        app_id: response.data.app_id,
        app_secret: response.data.app_secret || '',
        admin_access_token: response.data.admin_access_token || '',
        priority: response.data.priority,
        daily_api_limit: response.data.daily_api_limit,
        status: response.data.status,
      });
      setModalVisible(true);
    } catch (error) {
      message.error('获取应用详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await api.delete(`/applications/${record.id}`);
      message.success('删除成功');
      fetchApplications();
    } catch (error) {
      message.error(error.response?.data?.detail || '删除失败');
    }
  };

  const handleSyncBmAssets = async (record) => {
    setSyncingAppId(record.id);
    try {
      const response = await api.post(`/applications/${record.id}/sync-bm-assets`);
      const data = response.data || {};
      const details = data.sync_details || [];
      const successCount = details.filter((d) => d.status === 'success').length;
      const failedCount = details.length - successCount;
      Modal.success({
        title: 'BM 资产同步完成',
        content: (
          <div>
            <p>应用：{data.application_name || record.name}</p>
            <p>发现 BM：{data.bms_found ?? 0}，新建：{data.bms_created ?? 0}，更新：{data.bms_updated ?? 0}</p>
            <p>新关联到本应用：{data.bms_linked_to_application ?? 0}</p>
            <p>资产同步成功：{successCount} 个 BM{failedCount > 0 ? `，失败：${failedCount} 个` : ''}</p>
          </div>
        ),
      });
      fetchApplications();
    } catch (error) {
      message.error(error.response?.data?.detail || '同步 BM 资产失败');
    } finally {
      setSyncingAppId(null);
    }
  };

  const handleViewStats = async () => {
    try {
      const response = await api.get('/applications/stats');
      setStats(response.data || []);
      setStatsVisible(true);
    } catch (error) {
      message.error('获取统计失败');
    }
  };

  const columns = [
    { title: '应用名称', dataIndex: 'name', key: 'name' },
    { title: 'App ID', dataIndex: 'app_id', key: 'app_id' },
    {
      title: 'X-App-Usage',
      key: 'fb_usage',
      width: 180,
      render: (_, record) => (
        <FbUsageCell fbUsage={record.fb_usage} mode="app" />
      ),
    },
    {
      title: 'API 配额',
      key: 'quota',
      render: (_, record) => (
        <Progress
          percent={Math.round(((record.daily_api_calls || 0) / (record.daily_api_limit || 1)) * 100)}
          size="small"
          status={(record.daily_api_calls || 0) >= (record.daily_api_limit || 0) ? 'exception' : 'active'}
          format={() => `${record.daily_api_calls || 0}/${record.daily_api_limit || 0}`}
        />
      ),
    },
    {
      title: '关联',
      key: 'links',
      render: (_, record) => (
        <span>
          BM {record.bm_count || 0} / 开发者 {record.developer_count || 0} / 账户 {record.ad_account_count || 0}
        </span>
      ),
    },
    { title: '优先级', dataIndex: 'priority', key: 'priority' },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '正常' : status}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            icon={<SyncOutlined />}
            loading={syncingAppId === record.id}
            onClick={() => handleSyncBmAssets(record)}
          >
            同步BM资产
          </Button>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除吗？" onConfirm={() => handleDelete(record)}>
            <Button icon={<DeleteOutlined />} danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Facebook 应用管理"
        extra={
          <Space>
            <Button icon={<BarChartOutlined />} onClick={handleViewStats}>统计</Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingApp(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              添加应用
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={applications}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={editingApp ? '编辑应用' : '添加应用'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingApp(null);
        }}
        width={600}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical" initialValues={{ status: 'active', priority: 1, daily_api_limit: 1000 }}>
          <Form.Item name="name" label="应用名称" rules={[{ required: true, message: '请输入应用名称' }]}>
            <Input placeholder="例如：主应用" />
          </Form.Item>
          <Form.Item name="app_id" label="App ID" rules={[{ required: true, message: '请输入 App ID' }]}>
            <Input placeholder="Facebook App ID" />
          </Form.Item>
          <Form.Item
            name="app_secret"
            label="App Secret"
            rules={[{ required: !editingApp, message: '请输入 App Secret' }]}
          >
            <Input.Password placeholder={editingApp ? '留空则不修改' : 'Facebook App Secret'} />
          </Form.Item>
          <Form.Item
            name="admin_access_token"
            label="Admin Access Token"
            rules={[{ required: !editingApp, message: '请输入 Admin Access Token' }]}
          >
            <Input.TextArea rows={3} placeholder={editingApp ? '留空则不修改' : '应用管理员 Access Token'} />
          </Form.Item>
          <Form.Item name="priority" label="优先级">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="daily_api_limit" label="每日 API 上限">
            <Input type="number" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select>
              <Option value="active">正常</Option>
              <Option value="paused">暂停</Option>
              <Option value="disabled">停用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="应用 API 调用统计（今日）"
        open={statsVisible}
        onCancel={() => setStatsVisible(false)}
        footer={null}
        width={800}
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          {stats.map((item) => (
            <Col span={8} key={item.id}>
              <Card size="small">
                <Statistic title={item.name} value={item.daily_api_calls} suffix={`/ ${item.daily_api_limit}`} />
                <Progress percent={item.usage_rate} size="small" />
              </Card>
            </Col>
          ))}
        </Row>
        <Table
          dataSource={stats}
          rowKey="id"
          pagination={false}
          columns={[
            { title: '应用', dataIndex: 'name' },
            { title: 'App ID', dataIndex: 'app_id' },
            { title: '今日调用', dataIndex: 'daily_api_calls' },
            { title: '上限', dataIndex: 'daily_api_limit' },
            { title: '使用率', dataIndex: 'usage_rate', render: (v) => `${v}%` },
            {
              title: '可用',
              dataIndex: 'can_use',
              render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '是' : '否'}</Tag>,
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default Applications;
